import type { AdminPharmacy } from './types';

import { apiClient } from '~shared/api';

export const getAllPharmacyFeatures = (): Promise<unknown> =>
  apiClient.get<unknown>('/admin/valwin/pharmacies/features', {
    groupId: 'valwin',
  });

export const getAllPharmacyDeliveryServices = (): Promise<unknown> =>
  apiClient.get<unknown>('/admin/valwin/pharmacies/delivery', {
    groupId: 'valwin',
  });

export const getAllPharmacyMeetingTopics = (): Promise<unknown> =>
  apiClient.get<unknown>('/admin/valwin/pharmacies/meeting-topics', {
    groupId: 'valwin',
  });

export const getAllPharmacies = (): Promise<AdminPharmacy[]> =>
  apiClient.get<AdminPharmacy[]>('/admin/valwin/pharmacies', {
    groupId: 'valwin',
  });

export const createPharmacy = (body: unknown): Promise<unknown> =>
  apiClient.post<unknown>('/admin/valwin/pharmacies', body, {
    groupId: 'valwin',
  });

export const getNearestPharmacies = (
  lat: number,
  long: number
): Promise<unknown[]> =>
  apiClient.get<unknown[]>(
    `/admin/valwin/pharmacies/nearest?lat=${lat}&long=${long}`,
    { groupId: 'valwin' }
  );

export const getEnabledPharmacyMeetings = (
  pharmacyId: string
): Promise<unknown> =>
  apiClient.get<unknown>(
    `/admin/valwin/pharmacies/${pharmacyId}/meeting-topics`,
    { groupId: 'valwin' }
  );

export const getEnabledPharmacyFeatures = (
  pharmacyId: string
): Promise<unknown> =>
  apiClient.get<unknown>(`/admin/valwin/pharmacies/${pharmacyId}/features`, {
    groupId: 'valwin',
  });

export const enablePharmacyFeature = (
  pharmacyId: string,
  featureId: string
): Promise<unknown> =>
  apiClient.post<unknown>(
    `/admin/valwin/pharmacies/${pharmacyId}/features/${featureId}/enable`,
    null,
    { groupId: 'valwin' }
  );

export const disablePharmacyFeature = (
  pharmacyId: string,
  featureId: string
): Promise<unknown> =>
  apiClient.post<unknown>(
    `/admin/valwin/pharmacies/${pharmacyId}/features/${featureId}/disable`,
    null,
    { groupId: 'valwin' }
  );

export const getEnabledPharmacyDeliveryServices = (
  pharmacyId: string
): Promise<unknown> =>
  apiClient.get<unknown>(`/admin/valwin/pharmacies/${pharmacyId}/delivery`, {
    groupId: 'valwin',
  });

export const enablePharmacyDeliveryService = (
  pharmacyId: string,
  service: string
): Promise<unknown> =>
  apiClient.post<unknown>(
    `/admin/valwin/pharmacies/${pharmacyId}/delivery/${service}/enable`,
    null,
    { groupId: 'valwin' }
  );

export const configureZipCodeGeofence = (
  pharmacyId: string,
  service: string,
  allowedZipCodes: unknown
): Promise<unknown> =>
  apiClient.post<unknown>(
    `/admin/valwin/pharmacies/${pharmacyId}/delivery/${service}/geofence`,
    allowedZipCodes,
    { groupId: 'valwin' }
  );

export const configureAllowedProducts = (
  pharmacyId: string,
  service: string,
  products: unknown,
  isWhitelist: boolean
): Promise<unknown> =>
  apiClient.post<unknown>(
    `/admin/valwin/pharmacies/${pharmacyId}/delivery/${service}/products`,
    { products, isWhitelist },
    { groupId: 'valwin' }
  );

export const disablePharmacyDeliveryService = (
  pharmacyId: string,
  service: string
): Promise<unknown> =>
  apiClient.post<unknown>(
    `/admin/valwin/pharmacies/${pharmacyId}/delivery/${service}/disable`,
    null,
    { groupId: 'valwin' }
  );

export const getPharmacyDomain = (pharmacyId: string): Promise<unknown> =>
  apiClient.get<unknown>(`/admin/valwin/pharmacies/${pharmacyId}/domain`, {
    groupId: 'valwin',
  });

export const putPharmacyDomain = (
  pharmacyId: string,
  data: unknown
): Promise<unknown> =>
  apiClient.put<unknown>(
    `/admin/valwin/pharmacies/${pharmacyId}/domain`,
    data,
    { groupId: 'valwin' }
  );

export const enablePharmacyMeeting = (
  pharmacyId: string,
  meetingId: string
): Promise<unknown> =>
  apiClient.post<unknown>(
    `/admin/valwin/pharmacies/${pharmacyId}/meeting-topics/${meetingId}/enable`,
    null,
    { groupId: 'valwin' }
  );

export const disablePharmacyMeeting = (
  pharmacyId: string,
  meetingId: string
): Promise<unknown> =>
  apiClient.post<unknown>(
    `/admin/valwin/pharmacies/${pharmacyId}/meeting-topics/${meetingId}/disable`,
    null,
    { groupId: 'valwin' }
  );

export const getAllPharmaciesStats = ({
  before,
  after,
}: {
  before?: string;
  after?: string;
}): Promise<unknown> => {
  const qs = new URLSearchParams(
    (
      [
        ['before', before],
        ['after', after],
      ] as [string, string | undefined][]
    ).filter((d): d is [string, string] => !!d[1])
  );

  return apiClient.get<unknown>(`/admin/valwin/stats/pharmacies?${qs}`, {
    groupId: 'valwin',
  });
};

export const updateAllSupportAccounts = (data: unknown): Promise<unknown> =>
  apiClient.post<unknown>('/admin/valwin/pharmacies/support-accounts', data, {
    groupId: 'valwin',
  });

export const updateSupportAccountsStatus = (): Promise<unknown> =>
  apiClient.get<unknown>('/admin/valwin/pharmacies/support-accounts/status', {
    groupId: 'valwin',
  });
